import React from 'react';
import Modal from '../Modal';
import SearchableSelect from '../SearchableSelect';
import {
  getAiConditionFields
} from '../../utils/ruleConditions';
import { CONDITION_OPERATOR_OPTIONS } from './ruleEditorOptions';

const EMPTY_OPERATORS = new Set(['is_empty', 'is_not_empty']);
const ABSOLUTE_DATE_OPERATORS = new Set(['date_gt', 'date_lt']);
const RELATIVE_DATE_OPERATORS = new Set([
  'date_gt_days',
  'date_lt_days',
  'date_past_within',
  'date_future_within'
]);

const RuleConditionModal = ({
  isOpen,
  isEditing,
  condition,
  fields,
  aiConfigs,
  persons = [],
  aiConfigsError = '',
  conditionFieldOptions,
  isComplete,
  onChange,
  onOperatorChange,
  onFieldChange,
  onSubmit,
  onClose
}) => {
  const selectedField = fields.find(field => field.field_id === condition.field);
  const personValueKey = {
    accountId: 'account_id',
    displayName: 'name',
    emailAddress: 'email'
  }[selectedField?.value_path];
  const usePersonSelect = ['assignee', 'reporter'].includes(condition.field)
    && ['equals', 'not_equals'].includes(condition.operator)
    && Boolean(personValueKey);
  const selectedPersons = (condition.value || '').split(',').map(value => value.trim()).filter(Boolean);
  const personOptions = Array.from(new Map(persons
    .filter(person => person[personValueKey])
    .map(person => [person[personValueKey], {
      value: person[personValueKey],
      label: `${person.name} (${person.account_id})`
    }])).values());
  selectedPersons.forEach(value => {
    if (!personOptions.some(option => option.value === value)) {
      personOptions.push({ value, label: `${value}（已有条件值）` });
    }
  });
  const supportsImages = aiConfigs.some(ai => ai.provider === condition.ai_provider && ai.supports_images);
  const isAiJudge = condition.operator === 'ai_judge';
  const needsValue = !isAiJudge && !EMPTY_OPERATORS.has(condition.operator);
  const valuePlaceholder = ABSOLUTE_DATE_OPERATORS.has(condition.operator)
    ? '格式: 2026-03-27'
    : RELATIVE_DATE_OPERATORS.has(condition.operator)
      ? '天数，如: 30 表示30天内'
      : '输入条件值';

  const updateCondition = (patch) => {
    onChange(previous => ({ ...previous, ...patch }));
  };

  return (
    <Modal
      isOpen={isOpen}
      title={isEditing ? '编辑匹配条件' : '添加匹配条件'}
      className="rule-condition-modal"
      size="large"
      onClose={onClose}
      buttons={[
        { label: '取消', onClick: onClose, variant: 'default' },
        {
          label: isEditing ? '更新条件' : '添加条件',
          onClick: onSubmit,
          variant: 'primary',
          disabled: !isComplete || (isAiJudge && condition.include_images && !supportsImages)
        }
      ]}
    >
      <div className={`rule-condition-form ${isAiJudge ? 'is-ai' : ''}`}>
        <div className="form-group">
          <label className="form-label">
            {isAiJudge ? '参与判断的字段' : '选择字段'}
          </label>
          <SearchableSelect
            value={isAiJudge
              ? getAiConditionFields(condition)
              : condition.field}
            options={fields.map(field => ({
              value: field.field_id,
              label: field.display_name
            }))}
            multiple={isAiJudge}
            onChange={onFieldChange}
            placeholder={isAiJudge ? '选择参与判断的字段' : '选择字段'}
          />
        </div>

        <div className="form-group">
          <label className="form-label">操作符</label>
          <SearchableSelect
            value={condition.operator}
            options={CONDITION_OPERATOR_OPTIONS}
            onChange={onOperatorChange}
            placeholder="选择操作符"
            searchable={false}
          />
        </div>

        {isAiJudge && (
          <div className="form-group">
            <label className="form-label">选择 AI</label>
            <SearchableSelect
              value={condition.ai_provider}
              options={aiConfigsError
                ? [{
                    value: '',
                    label: aiConfigsError,
                    disabled: true
                  }]
                : aiConfigs.length === 0
                  ? [{
                    value: '',
                    label: '暂无启用的 AI，请先配置',
                    disabled: true
                    }]
                  : aiConfigs.map(ai => ({
                    value: ai.provider,
                    label: ai.label || `${ai.provider} (${ai.model})`
                  }))
              }
              onChange={(aiProvider) => updateCondition({
                ai_provider: aiProvider,
                include_images: aiProvider === condition.ai_provider ? condition.include_images : false
              })}
              placeholder="选择 AI 提供商"
            />
          </div>
        )}

        {isAiJudge && (supportsImages || condition.include_images) && (
          <div className="form-group">
            <label className="form-label">
              <input type="checkbox" checked={condition.include_images === true}
                onChange={event => updateCondition({ include_images: event.target.checked })} />
              包含工单图片（仅 Codex Gateway）
            </label>
            <small className="form-help">读取描述中的图片和图片附件，最多 5 张、单张 5 MiB、合计 10 MiB；获取失败会记录判断错误。</small>
            {!supportsImages && <small className="form-help">当前 AI 通道不支持图片，请选择 Codex Gateway 或关闭此选项。</small>}
          </div>
        )}

        {needsValue && (
          <div className="form-group">
            <label className="form-label">条件值</label>
            {usePersonSelect ? (
              <>
                <SearchableSelect
                  multiple
                  value={selectedPersons}
                  options={personOptions}
                  onChange={(values) => updateCondition({ value: values.join(',') })}
                  placeholder="选择人员（可多选）"
                  emptyText="暂无可选人员，请在人员配置中维护"
                />
                <small className="form-help">
                  选项来自人员配置；按{personValueKey === 'account_id' ? '账号 ID' : personValueKey === 'name' ? '姓名（需与 Jira 显示名称一致）' : '邮箱'}匹配。
                  等于匹配任一所选人员，不等于排除所有所选人员。
                </small>
              </>
            ) : conditionFieldOptions.length > 0 ? (
              <SearchableSelect
                value={condition.value}
                options={conditionFieldOptions.map(option => ({
                  value: option.value,
                  label: option.label || option.value
                }))}
                onChange={(value) => updateCondition({ value })}
                placeholder="选择条件值"
                emptyText="没有匹配的字段选项"
              />
            ) : (
              <input
                type="text"
                value={condition.value}
                onChange={(event) => updateCondition({
                  value: event.target.value
                })}
                className="form-input"
                placeholder={valuePlaceholder}
              />
            )}
          </div>
        )}

        {isAiJudge && (
          <div className="form-group">
            <label className="form-label">判断模式</label>
            <SearchableSelect value={condition.ai_mode || 'judge'} searchable={false}
              options={[{ value: 'judge', label: '仅判断' }, { value: 'judge_and_comment', label: '判断并生成评论' }]}
              onChange={ai_mode => updateCondition({ ai_mode })} />
          </div>
        )}

        {isAiJudge && (
          <div className="form-group rule-condition-prompt">
            <label className="form-label">AI 提示词</label>
            <textarea
              value={condition.value}
              onChange={(event) => updateCondition({
                value: event.target.value
              })}
              className="form-input"
              placeholder={condition.ai_mode === 'judge_and_comment'
                ? '判断需求是否可通过开放能力实现，有明确依据才通过；评论中说明实现方案、文档依据和限制。'
                : '请判断这个工单的内容是否涉及技术故障。如果是，请只回复：是；如果不是，请只回复：否。'}
              rows={5}
            />
            <small className="form-help">
              {condition.ai_mode === 'judge_and_comment'
                ? '填写判断标准和评论要求即可。输出格式由系统统一约束，请勿要求只返回“是”或“否”；格式错误会记录为判断失败。'
                : '明确要求 AI 只返回“是”或“否”，避免附加解释。'}
            </small>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RuleConditionModal;
